/**
 * „Was 1.000 Kontakte je Produkt erwarten lassen" (P1-V3 § 2.5, Umbau 25.10.2026): Erwarteter Wert je 1.000 Kontakte
 * der Webinar-Einladungen und der Direkt-Mailings getrennt (verschiedene Listen, kein direkter Tausch). Beim Webinar
 * zählen nur Käufe über die Einladung; die Obergrenze „bis x € mit allen Webinar-Käufen" ist die benannte Ausnahme vom
 * Attributionsvertrag (E3, Till 23.10.). Vorläufig ergänzte Anfos in keinem Euro-Wert: ein Artikel, dessen Anfos alle
 * vorläufig sind, steht mit „—" und fehlt in der Summenzeile bei Wert, Anfos, Kontakten und Abmeldungen.
 */
import { FC, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import { useAuswertungenMeta } from '../../../components/auswertungen/ChapterPage';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { alterText, BefundZeile, euroJeTausend, jeTausend, kompakt, nachGruppenReihenfolge, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import { Legende, TipRow, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { ATTRIBUTION, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { EffizienzMarken, EffizienzSumme, EffizienzTeil, EffizienzZeile, fetchProduktEffizienz } from '../../../helpers/auswertungenData';
import { balkenHoehe, Hinweis, int, Laden, ProduktZelle, Strich, tagMonat, Unter, useLadenMit } from './shared';
import { effizienzBefund, MIN_ANFOS_EFF, MIN_KONTAKTE_EFF, tragfaehig } from './befunde';

/** Darunter entscheiden einzelne Anfos (Regel 3): grau unter 20 Anfos oder unter 100.000 Kontakten (Schwellen in `befunde.ts`). */
const MIN_KONTAKTE = MIN_KONTAKTE_EFF;
const MIN_ANFOS = MIN_ANFOS_EFF;
const MIN_ABMELDUNGEN = 20;

const GRUPPEN = ['Wert', 'Menge', 'Abmeldungen', 'Zahlung (wächst mit dem Alter)'] as const;
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['produkt', 'webW', 'webK', 'webAbm', 'dmW', 'dmK', 'dmAbm'],
  mittel: ['produkt', 'webW', 'webAbm', 'dmW', 'dmAbm'],
  schmal: ['produkt', 'webW', 'dmW'],
};
const WEB = 'Webinar-Einladungen';
const DM = 'Direkt-Mailings';

const fest = (t: EffizienzTeil) => t.anfos - (t.provisional ?? 0);
const unsicherGrund = (t: EffizienzTeil): string | null =>
  t.contacts === 0 || t.m4_per_1k === null ? null : t.contacts < MIN_KONTAKTE ? `unter ${kompakt(MIN_KONTAKTE)} Kontakten` : fest(t) < MIN_ANFOS ? `unter ${MIN_ANFOS} Anfos` : null;

const OBERGRENZE_KLEINER = 'Die Obergrenze liegt hier unter dem Wert über die Einladung: die Anfos über die Einladung zählen lebenslang, die zugeordneten Anfos nur bis zum Stichtag. Nicht deutbar.';

const EffizienzKarte: FC = () => {
  // Workshop E1 (29.10.2026): alle Marken / nur FT. In der FT-Ansicht kommen Zähler UND Nenner aus den FT-Versandzeilen;
  // die Obergrenze „mit allen Webinar-Käufen" liefert das Backend dann nicht (Webinar-Anfos sind nicht nach Marke trennbar).
  const [marken, setMarken] = useState<EffizienzMarken>('alle');
  const ft = marken === 'ft';
  const { data, fehler } = useLadenMit(fetchProduktEffizienz, marken);
  // B50: „Anfos bis" = Datenstand der Bestellungen (letzter eingeschlossener Tag), nicht der Stichtag.
  const plxStand = useAuswertungenMeta()?.sources.find((q) => q.key === 'plx_orders')?.updated_at ?? null;
  const viz = useVizStyle();
  const breit = useMediaQuery(useTheme().breakpoints.up('md'));
  const summe = data?.summary as EffizienzSumme | undefined;

  const chartData = useMemo(
    () =>
      (data?.rows ?? [])
        .filter((r) => (tragfaehig(r.web) && (r.web.m4_per_1k as number) > 0) || (tragfaehig(r.plx) && (r.plx.m4_per_1k as number) > 0))
        .map((r) => ({
          produkt: r.plx_product,
          name: r.name,
          web: tragfaehig(r.web) ? (r.web.m4_per_1k as number) : 0,
          ober: tragfaehig(r.web) && r.obergrenze_per_1k != null && r.obergrenze_per_1k >= (r.web.m4_per_1k as number) ? r.obergrenze_per_1k : null,
          plx: tragfaehig(r.plx) ? (r.plx.m4_per_1k as number) : 0,
          webK: r.web.contacts,
          plxK: r.plx.contacts,
        }))
        .sort((a, b) => a.web - b.web || a.plx - b.plx),
    [data],
  );
  const oberMax = useMemo(() => Math.max(0, ...chartData.map((d) => Math.max(d.web, d.plx, d.ober ?? 0))), [chartData]);

  const wertZelle = (t: EffizienzTeil, r: EffizienzZeile | null, web: boolean) => {
    if (!web && r?.nur_vorlaeufig)
      return (
        <Box sx={{ lineHeight: 1.25 }}>
          <Strich grund={`Alle Anfos dieses Artikels sind vorläufig ergänzt — sie zählen in keinem Euro-Wert. ${TERMS.vorlaeufig.tip}`} />
          <Unter>{int(t.anfos)} Anfos, alle vorläufig</Unter>
        </Box>
      );
    if (t.contacts === 0) return <Strich grund={web ? 'Keine Webinar-Einladung dieses Produkts im Zeitraum.' : 'Kein Direkt-Mailing dieses Produkts im Zeitraum.'} />;
    const ober = r ? r.obergrenze_per_1k : (summe?.obergrenze_per_1k ?? null);
    return (
      <Box sx={{ lineHeight: 1.25 }}>
        {euroJeTausend(t.m4_per_1k)}
        <Unter>
          {int(fest(t))} Anfos
          {web && ober !== null && ober !== undefined && t.m4_per_1k !== null && (
            <>
              {' · '}
              {ober >= t.m4_per_1k ? (
                <Tooltip title={TERMS.obergrenzeEinladung.tip} arrow placement="top">
                  <span style={{ cursor: 'help' }}>bis {euroJeTausend(ober)}</span>
                </Tooltip>
              ) : (
                <>
                  bis <Strich grund={OBERGRENZE_KLEINER} />
                </>
              )}
            </>
          )}
        </Unter>
      </Box>
    );
  };

  const teil = (k: 'web' | 'plx'): Spalte<EffizienzZeile>[] => {
    const web = k === 'web';
    const p = web ? 'web' : 'dm';
    const gruppe = web ? WEB : DM;
    return [
      {
        key: `${p}W`,
        label: TERMS.wertJe1k.label,
        einheit: 'je 1.000 Kontakte',
        tip: web ? TERMS.wertJe1kEinladung.tip : TERMS.wertJe1k.tip,
        hinweis: web
          ? ft
            ? 'Darunter: Anfos über die Einladung (nur FT-Versände).'
            : `Darunter: Anfos über die Einladung · ${TERMS.obergrenzeEinladung.label.replace('…', 'x €')}.`
          : data
            ? `${int(data.plx_zuordnung.zugeordnet)} von ${int(data.plx_zuordnung.anfos)} Direkt-Mailing-Anfos des Zeitraums ließen sich einem Versand zuordnen (${formatPct((data.plx_zuordnung.zugeordnet / Math.max(1, data.plx_zuordnung.anfos)) * 100)}), ohne vorläufig ergänzte.`
            : undefined,
        kopfGruppe: gruppe,
        gruppe: 'Wert',
        pflicht: true,
        align: 'right',
        unsicher: (r) => unsicherGrund(r[k]),
        render: (r) => wertZelle(r[k], r, web),
        sortValue: (r) => (r[k].contacts > 0 && !(k === 'plx' && r.nur_vorlaeufig) ? r[k].m4_per_1k : null),
        summary: summe ? wertZelle(summe[k], null, web) : null,
      },
      {
        key: `${p}K`,
        label: TERMS.kontakteRegulaer.label,
        tip: TERMS.kontakteRegulaer.tip,
        kopfGruppe: gruppe,
        gruppe: 'Menge',
        align: 'right',
        render: (r) => (r[k].contacts > 0 ? kompakt(r[k].contacts) : '–'),
        // „–“ (keine Versände) liefert null und steht damit in beiden Richtungen am Ende; mit 0 stand es aufsteigend oben (Abnahme 29.10.).
        sortValue: (r) => (r[k].contacts > 0 ? r[k].contacts : null),
        summary: kompakt(summe?.[k].contacts),
      },
      {
        key: `${p}Abm`,
        label: TERMS.abmeldungenJe1kVersand.label,
        einheit: 'je 1.000',
        tip: TERMS.abmeldungenJe1kVersand.tip,
        kopfGruppe: gruppe,
        gruppe: 'Abmeldungen',
        align: 'right',
        unsicher: (r) => (r[k].contacts > 0 && (r[k].unsubscribes ?? 0) < MIN_ABMELDUNGEN ? `unter ${MIN_ABMELDUNGEN} Abmeldungen` : null),
        render: (r) => (r[k].contacts > 0 ? jeTausend(r[k].unsubscribes_per_1k ?? null) : '–'),
        sortValue: (r) => (r[k].contacts > 0 ? (r[k].unsubscribes_per_1k ?? null) : null),
        summary: jeTausend(summe?.[k].unsubscribes_per_1k ?? null),
      },
      {
        key: `${p}Sum`,
        label: TERMS.wertSumme.label,
        tip: `${TERMS.wertSumme.tip} ${web ? 'Nur Anfos über die Einladung.' : 'Nur dem Versand zugeordnete Anfos.'}`,
        kopfGruppe: gruppe,
        gruppe: 'Wert',
        align: 'right',
        render: (r) => (r[k].contacts > 0 ? tEuro(r[k].m4_value) : '–'),
        sortValue: (r) => r[k].m4_value,
        summary: tEuro(summe?.[k].m4_value),
      },
      {
        key: `${p}A1k`,
        label: TERMS.anfosJe1k.label,
        tip: TERMS.anfosJe1k.tip,
        kopfGruppe: gruppe,
        gruppe: 'Menge',
        align: 'right',
        render: (r) => (r[k].contacts > 0 ? jeTausend(r[k].anfos_per_1k) : '–'),
        sortValue: (r) => r[k].anfos_per_1k,
        summary: jeTausend(summe?.[k].anfos_per_1k),
      },
      {
        key: `${p}Vers`,
        label: TERMS.versaende.label,
        tip: TERMS.versaende.tip,
        kopfGruppe: gruppe,
        gruppe: 'Menge',
        align: 'right',
        render: (r) => int(r[k].sends),
        sortValue: (r) => r[k].sends,
        summary: int(summe?.[k].sends),
      },
      {
        key: `${p}Paid`,
        label: TERMS.bezahltJe1k.label,
        einheit: 'Ø Alter',
        // B70: Bündel mit Ø Alter (Regel 2); der Text je Seite — Direkt-Mailings haben keine „jungen Webinare".
        tip: web
          ? `${TERMS.bezahltJe1k.tip} Ø Alter: der Anfos der Termine, gewichtet mit ihren Anfos über die Einladung.`
          : 'Echtes Geld: was die dem Versand zugeordneten Direkt-Mailing-Anfos bis heute gezahlt haben (netto), je 1.000 Kontakte. Junge Versände liegen hier immer niedrig — die Rechnungen kommen erst nach der Testphase und dann quartalsweise.',
        kopfGruppe: gruppe,
        gruppe: 'Zahlung (wächst mit dem Alter)',
        nurMit: `${p}W`,
        align: 'right',
        render: (r) => (r[k].contacts > 0 ? <BezahltJe1k t={r[k]} /> : '–'),
        sortValue: (r) => r[k].paid_per_1k,
        summary: summe ? <BezahltJe1k t={summe[k]} /> : null,
      },
    ];
  };
  const baue = (breit = true): Spalte<EffizienzZeile>[] => [
    {
      key: 'produkt',
      label: 'Produkt',
      gruppe: 'Wert',
      pflicht: true,
      minWidth: breit ? 120 : undefined,
      render: (r) => <ProduktZelle code={r.plx_product} name={r.name} />,
      sortValue: (r) => r.plx_product,
    },
    ...teil('web'),
    ...teil('plx'),
  ];
  // B69: dieselbe Spalte gibt es je Seite (Einladung / Direkt-Mailing) — im Menü mit Seitennamen, sonst stehen sechs
  // Einträge doppelt und ununterscheidbar da. Nur die Menü-Beschriftung; der Tabellenkopf trägt die Seite als Gruppe.
  const meta = useMemo(() => {
    const spalten = baue();
    const zaehl = new Map<string, number>();
    for (const c of spalten) zaehl.set(c.label, (zaehl.get(c.label) ?? 0) + 1);
    const seite = new Map(spalten.map((c) => [c.key, c.kopfGruppe]));
    return nachGruppenReihenfolge(
      spaltenMeta(spalten).map((m) => ((zaehl.get(m.label) ?? 0) > 1 && seite.get(m.key) ? { ...m, label: `${seite.get(m.key)} · ${m.label}` } : m)),
      GRUPPEN,
    );
  }, [data, ft]); // baue() liest nur `data` und `ft` (und davon abgeleitete Werte)
  const wahl = useSpaltenwahl('effizienz', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(wahl.breite === 'breit').filter((c) => sichtbar.has(c.key));
  const ohne = data?.ohne_vorlaeufige_artikel ?? [];

  return (
    <ChartCard
      title={ft ? 'Was 1.000 Kontakte je Produkt erwarten lassen — nur Finanztrends' : 'Was 1.000 Kontakte je Produkt erwarten lassen'}
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={marken} onChange={(_e, v) => v && setMarken(v)} aria-label="Welche Listen">
            <ToggleButton value="alle" sx={{ textTransform: 'none', py: 0.25 }}>
              alle Marken
            </ToggleButton>
            <ToggleButton value="ft" sx={{ textTransform: 'none', py: 0.25 }}>
              {TERMS.nurFinanztrends.short}
            </ToggleButton>
          </ToggleButtonGroup>
          <SpaltenMenue spalten={meta} wahl={wahl} />
        </Box>
      }
      howToRead="Je Produkt: Was bringen 1.000 Kontakte über die Webinar-Einladung, was über ein Direkt-Mailing? Beide gehen an verschiedene Listen. Die Karte ist ein Richtwert für die Gewichtung der Versände, kein direkter Tausch."
      terms={[
        { label: TERMS.kontakteRegulaer.label, tip: TERMS.kontakteRegulaer.tip },
        { label: TERMS.wertJe1k.label, tip: TERMS.wertJe1k.tip },
        ...(ft ? [] : [{ label: TERMS.obergrenzeEinladung.short, tip: TERMS.obergrenzeEinladung.tip }]),
        { label: TERMS.nurFinanztrends.label, tip: TERMS.nurFinanztrends.tip },
        { label: TERMS.abmeldungenJe1kVersand.label, tip: TERMS.abmeldungenJe1kVersand.tip },
        { label: ATTRIBUTION.ersterKontakt.label, tip: ATTRIBUTION.ersterKontakt.tip },
      ]}
      source="Webinar-Versände, Direkt-Mailing-Versände, PL-X-Bestellungen, Anfo-Wert je Anfo"
      freshnessKey={['webinar_send_metrics', 'plx_orders', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {/* B6: Direkt-Mailing gegen Einladung, nur Produkte mit tragfähigem Wert auf beiden Wegen (wie die Grafik). */}
            <BefundZeile
              text={effizienzBefund(data.rows)}
              fehlt={effizienzBefund(data.rows) ? null : 'Kein Produkt hat auf beiden Wegen einen tragfähigen Wert (mindestens 20 Anfos und 100.000 Kontakte).'}
              tip={`Gezählt werden Produkte mit mindestens ${MIN_ANFOS} Anfos und ${kompakt(MIN_KONTAKTE)} Kontakten auf beiden Wegen. ${TERMS.wertJe1k.tip}`}
            />
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Versände {tagMonat(data.window.von)}
              {data.window.von.slice(0, 4)}–{tagMonat(vorTag(data.window.bis))}
              {vorTag(data.window.bis).slice(0, 4)} · Anfos bis {tagMonat(plxStand && plxStand.slice(0, 10) < data.window.asOf ? plxStand : data.window.asOf)}
            </Box>
            <Box sx={{ fontSize: 14, mb: 1.5, maxWidth: '75ch', lineHeight: 1.5 }}>
              {/* B71: an den Code angeglichen — die erste Anfo einer über die Einladung angemeldeten Person zählt, egal über
                  welchen Link sie bestellt (auch über die Aufzeichnung), webinarAttribution.ts. */}
              Beim Webinar zählen die Käufe von Personen, die sich über eine dieser Einladungen angemeldet haben — egal über welchen
              Link sie bestellt haben, auch über die Aufzeichnung. Es fehlen Käufe von Personen ohne Anmeldung über eine Einladung
              (z. B. nur über Anzeigen oder direkt zur Aufzeichnung).{' '}
              {ft
                ? 'Gezählt werden nur die Versände an die Finanztrends-Liste und die Anfos, die über sie kamen — dieselben Termine wie bei „alle Marken“. Die Obergrenze mit allen Webinar-Käufen fehlt hier: die einem Termin zugeordneten Anfos lassen sich nicht nach Marke trennen.'
                : 'Rechts daneben steht, wie hoch der Wert höchstens wäre, wenn man alle Webinar-Käufe zählt.'}
            </Box>
            <Legende
              items={[
                { label: WEB, color: viz.series1 },
                ...(ft ? [] : [{ label: 'bis … mit allen Webinar-Käufen', color: viz.muted }]),
                { label: DM, color: viz.series2 },
              ]}
            />
            <Box sx={{ height: balkenHoehe(chartData.length * 1.7) }}>
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={['web', 'plx']}
                indexBy="produkt"
                layout="horizontal"
                groupMode="grouped"
                margin={{ top: 4, right: 72, bottom: 36, left: 72 }}
                padding={0.25}
                innerPadding={2}
                borderRadius={4}
                maxValue={oberMax > 0 ? oberMax : 'auto'}
                colors={[viz.series1, viz.series2]}
                theme={viz.nivo}
                animate={false}
                enableGridY={false}
                enableGridX
                gridXValues={breit ? 5 : 3}
                enableLabel={false}
                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: breit ? 5 : 3, format: (v) => `${Number(v).toLocaleString('de-DE')} €` }}
                layers={[
                  'grid',
                  'axes',
                  // Spanne bis zur Obergrenze als heller Fortsatz hinter dem Webinar-Balken (E3).
                  ({ bars, xScale }) => (
                    <g>
                      {bars
                        .filter((b) => b.data.id === 'web' && b.data.data.ober != null)
                        .map((b) => {
                          const x2 = (xScale as (v: number) => number)(Number(b.data.data.ober));
                          return <rect key={`ober-${b.key}`} x={b.x} y={b.y} width={Math.max(0, x2 - b.x)} height={b.height} rx={4} fill={viz.muted} opacity={0.45} />;
                        })}
                    </g>
                  ),
                  'bars',
                  ({ bars }) => (
                    <g>
                      {bars
                        .filter((b) => Number(b.data.value) > 0)
                        .map((b) => (
                          <text key={b.key} x={b.x + b.width + 6} y={b.y + b.height / 2} dominantBaseline="central" fontSize={12} fontFamily={viz.fontFamily} fill={viz.inkSecondary}>
                            {euroJeTausend(Number(b.data.value))}
                          </text>
                        ))}
                    </g>
                  ),
                ]}
                tooltip={({ id, data: d }) => {
                  const web = id === 'web';
                  return (
                    <Box>
                      <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                        {String(d.produkt)} · {String(d.name)}
                      </Box>
                      <TipRow color={web ? viz.series1 : viz.series2} label={web ? `${WEB}, je 1.000` : `${DM}, je 1.000`} value={euroJeTausend(Number(web ? d.web : d.plx))} />
                      {web && d.ober != null && <TipRow color={viz.muted} label="bis … mit allen Webinar-Käufen" value={euroJeTausend(Number(d.ober))} />}
                      <TipRow label="Kontakte" value={int(Number(web ? d.webK : d.plxK))} />
                    </Box>
                  );
                }}
                role="img"
                ariaLabel={`Erwarteter Wert je 1.000 Kontakte je Produkt, Webinar-Einladungen und Direkt-Mailings getrennt${ft ? ', nur Finanztrends' : ''}`}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SortableTable
                rows={data.rows}
                columns={spalten}
                rowKey={(r) => r.plx_product}
                initialSort={{ key: 'webW', dir: 'desc' }}
                maxHeight={null}
                eng={wahl.breite !== 'breit'}
                summaryLabel="Insgesamt"
              />
            </Box>
            <Hinweis>
              {ft && 'Nur Finanztrends: Versände, Kontakte und Anfos der FT-Liste; Webinar-Termine wie bei „alle Marken“. '}
              Euro netto, Wert bei Bestellung ({data.anfo_wert_stand}). Ein Versand zählt erst, wenn er {data.window.reife_tage} Tage alt ist. Webinar:{' '}
              {int(data.webinare_im_fenster)} Termine, deren Einladungen alle in diesem Zeitraum liefen; gezählt beim beworbenen Produkt. Grau = unter{' '}
              {MIN_ANFOS} Anfos oder unter {kompakt(MIN_KONTAKTE)} Kontakten; solche Werte fehlen in der Grafik.
              {ohne.length > 0 &&
                ` Die Summenzeile Direkt-Mailing rechnet ohne ${ohne.map((a) => `${a.name} (${int(a.anfos)} Anfos, alle vorläufig)`).join(', ')} — bei Wert, Anfos, Kontakten und Abmeldungen.`}
              {data.plx_anmelde_versaende.sends > 0 &&
                ` ${int(data.plx_anmelde_versaende.sends)} Direkt-Mailings mit Link auf eine Webinar-Anmeldung (${kompakt(data.plx_anmelde_versaende.contacts)} Kontakte) zählen als Webinar-Einladung, nicht hier.`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

/** Tag vor einem YYYY-MM-DD (exklusives Fensterende → letzter Tag). */
const vorTag = (d: string): string => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() - 1);
  return t.toISOString().slice(0, 10);
};

/** „Bisher bezahlt je 1.000" als Bündel mit Ø Alter (Sektionsregel 2: bezahlt nie ohne Alter). */
const BezahltJe1k: FC<{ t: EffizienzTeil }> = ({ t }) => (
  <Box sx={{ lineHeight: 1.25 }}>
    {euroJeTausend(t.paid_per_1k)}
    <Unter>{alterText(t.avg_age_days ?? null)}</Unter>
  </Box>
);

export default EffizienzKarte;
